// Site-wide translations. Artwork titles, inventory codes and series slugs
// stay in original (international art convention). Per-painting curatorial
// notes default to English until artist-approved translations are added —
// see /data/notesI18n.js for the override hook.

export const LOCALES = [
  { code: "en", label: "EN", name: "English" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "it", label: "IT", name: "Italiano" },
  { code: "zh", label: "中文", name: "中文" },
  { code: "ja", label: "日本語", name: "日本語" },
];

export const STRINGS = {
  en: {
    // ─── Nav / chrome ──────────────────────────────────────────────
    "nav.works": "Works",
    "nav.rooms": "Exhibitions",
    "nav.about": "About",
    "nav.letter": "Contact",
    "nav.privacy": "Privacy",
    "nav.menu_open": "Menu / +",
    "nav.menu_close": "Close",
    "nav.index_count": "Index — {count} sections",
    "nav.studio": "Studio",
    "nav.skip_to_content": "Skip to content",
    "nav.language": "Language",
    "nav.studio_quote": "I am colour-blind.\nI have spent my whole life feeling colour rather than naming it.",

    // ─── Footer ────────────────────────────────────────────────────
    "footer.index": "Index",
    "footer.back_rooms": "Studio",
    "footer.elsewhere": "Elsewhere",
    "footer.rights": "© {year} Rushit Shah — All marks his own.",

    // ─── Loader ────────────────────────────────────────────────────
    "loader.archive_no": "R / S — Archive №01",
    "loader.entrance": "MMXXVI — Entrance",
    "loader.tagline": "An Archive of Layers, Argued With.",

    // ─── Hero ──────────────────────────────────────────────────────
    "hero.eyebrow_manifesto": "A · Manifesto",
    "hero.manifesto": "I paint in layers, then {argue}. Colour is felt, not named — an archive made between India, Singapore, and Germany.",
    "hero.manifesto_emph": "argue with them",
    "hero.cta": "Enter the Archive",
    "hero.look_closer": "Look closer ↗",
    "hero.ticker_1": "Selected works",
    "hero.ticker_2": "Made between three studios, three countries",
    "hero.ticker_3": "Oil · Acrylic · Lacquer · Drip · Scraped pigment",
    "hero.ticker_4": "Painted slowly · seen slowly",

    // ─── Ribbon Series (Section 02) ────────────────────────────────
    "ribbon.eyebrow": "Section 02 — Ribbon Series",
    "ribbon.hint": "Scroll → to advance the filmstrip",
    "ribbon.count": "{n} works",
    "ribbon.headline_pre": "A thread that",
    "ribbon.headline_emph": "keeps returning",

    // ─── Showcase (Section 03) ─────────────────────────────────────
    "showcase.eyebrow": "Section 03 — Selected Works",
    "showcase.scroll": "Scroll ↓",
    "showcase.headline_pre": "Layers, argued —",
    "showcase.headline_emph": "across canvas",
    "showcase.more_archive": "+ more works arriving in archive",
    "showcase.open_archive": "Open Full Archive ↗",

    // ─── Philosophy (Section 04) ───────────────────────────────────
    "philosophy.eyebrow": "04 — A Note from the Studio",
    "philosophy.phrase": "I paint in layers, then argue — with what colour does, not with its name.",

    // ─── Words / In the Artist's Own Words (Section 05) ────────────
    "words.eyebrow": "Section 05 — In the Artist's Words",
    "words.headline_pre": "In the artist's",
    "words.headline_emph": "own",
    "words.headline_post": "words.",
    "words.fragment_label": "Fragment",
    "words.I": "I am interested in the places where organic systems fail gracefully — neural maps, cracked skin, constellations, decay. My paintings are attempts to hold that failure still long enough to look at it. I am colourblind. I have spent my whole life feeling colour rather than naming it. I think that is why I trust it.",
    "words.II": "I spent years moving, believing that what I was looking for existed somewhere else — in another country, another landscape, another sky. The paintings came out of that exhaustion. Not as escape, but as arrival. For the first time, I was somewhere.",
    "words.III": "Jung taught me that what we cannot say directly, we say in image. Krishnamurti taught me that clear seeing requires the destruction of what I already think I know. I grew up looking at Eastern miniatures and ornament. I paint in an international abstract grammar. The friction between those two things is, so far, the thing I have most to say.",

    // ─── About / Biography ─────────────────────────────────────────
    "about.section": "About · Biography",
    "about.eyebrow": "b. 1986, Vadodara",
    "about.headline": "A painter who trusts what colour does.",
    "about.caption_portrait": "Paris · between rooms · 2024",
    "about.p1": "Rushit Shah was born on 8 September 1986 in Vadodara, Gujarat. He lives and works between India, Singapore, and Germany, and divides the studio year between the three. He is colourblind, and treats this not as a limitation but as the origin of his method: unable to rely on the names of colours, he learned to read what a colour weighs against its neighbour. The paintings are records of those small, almost physical decisions — built in successive layers of oil, lacquer, drip, scraped pigment, and dispersed mineral on linen and canvas, then argued with until something refuses to give way.",
    "about.p2": "Two inheritances meet in the work and do not resolve. The first is the visual world he grew up inside — the ornament, the miniature, the devotional surface that crowds every available inch with image. The second is the international vocabulary of abstraction he later entered as an adult. He does not want to choose. The friction between those two languages is, so far, the thing he has most to say.",
    "about.p3": "He cites Jung's idea that what cannot be said directly is said in image, and Krishnamurti's insistence that clear seeing requires the destruction of what one already thinks one knows, as the two thoughts that organise his studio days. He works slowly, in long accumulated sessions, and considers a painting finished only when the negotiation feels settled enough to be worth a name.",
    "about.p4": "The current body of work is presented as a single ongoing archive — made between three studios by someone who travelled too much to settle on one, and who paints as a way of staying in the conversation.",
    "about.statement_label": "Artist statement",
    "about.statement": "I paint in layers, then argue. I am interested in the places where organic systems fail gracefully — neural maps, cracked skin, constellations, decay. My paintings are attempts to hold that failure still long enough to look at it.",
    "about.statement_2": "I trust colour the way a stranger trusts weather: I feel it, I do not name it. Two traditions sit in the work — the ornamental density of Eastern miniature painting I grew up inside, and the looser grammar of international abstraction I came to later — and most of what I have to say is in the friction of refusing to choose.",
    "about.row_born": "Born",
    "about.row_born_value": "8 Sept 1986 · Vadodara",
    "about.row_studio": "Studio",
    "about.row_studio_value": "IN · SG · DE",
    "about.row_influence": "Influence",
    "about.row_influence_value": "Jung · Krishnamurti · the road",
    "about.row_condition": "Condition",
    "about.row_condition_value": "Colourblind. The work began there.",

    // ─── Exhibitions (was: Rooms) ──────────────────────────────────
    "exhib.section": "Section — Exhibitions · Where the work has lived",
    "exhib.eyebrow": "A quiet record",
    "exhib.headline_pre": "Exhibitions the work",
    "exhib.headline_emph": "has lived",
    "exhib.headline_post": "in.",
    "exhib.intro": "A record of the exhibitions — galleries, group shows, projects — the paintings have travelled through. Kept here in the order they happened.",
    "exhib.solo": "Solo",
    "exhib.group": "Group",
    "exhib.forthcoming": "Forthcoming",
    "exhib.empty_solo": "The first solo exhibition is being prepared.",
    "exhib.empty_group": "No group exhibitions recorded yet.",
    "exhib.empty_forthcoming": "The next exhibition has not yet been named.",
    "exhib.footer_line": "— The exhibitions, kept in order. Updated as they happen.",

    // ─── Works Index ───────────────────────────────────────────────
    "works.section": "Index — Works",
    "works.count": "{filtered} of {total}",
    "works.title": "Selected works.",
    "works.filter_year": "Filter by year",
    "works.filter_all": "All",
    "works.view_grid": "Grid",
    "works.view_stack": "Stack",
    "works.no_results": "No works in this year.",

    // ─── Work Detail ───────────────────────────────────────────────
    "work.position": "№ {n} / {total}",
    "work.back": "← Back to the archive",
    "work.prev": "← Previous",
    "work.next": "Next →",
    "work.adj_aria": "Adjacent works",
    "work.keys_hint": "← / → keys flip between works",
    "work.detail_alt": "{title} — detail {n}",
    "work.not_found": "This piece is not in the archive.",
    "work.label_medium": "Medium",
    "work.label_dimensions": "Dimensions",
    "work.label_edition": "Edition",
    "work.label_signature": "Signature",
    "work.label_inventory": "Inventory",
    "work.label_series": "Series",

    // ─── Contact / Letter ──────────────────────────────────────────
    "contact.section": "Section 07 — A letter, if you like.",
    "contact.headline_pre": "Let's",
    "contact.headline_emph": "talk",
    "contact.write_to": "Write to",
    "contact.studio_in_three": "The studio is in three places",
    "contact.studio_in_three_value": "India · Singapore · Germany",
    "contact.the_work": "The work",
    "contact.the_work_value": "Oil, acrylic, lacquer, drip, scraped pigment.\nOn linen and canvas, slowly.",
    "contact.elsewhere": "Elsewhere",
    "contact.field_name": "Your name",
    "contact.field_email": "Email",
    "contact.field_message": "Message",
    "contact.posture_label": "The posture of the letter",
    "contact.posture_acquire": "Acquire a Work",
    "contact.posture_exhibition": "Plan an Exhibition",
    "contact.posture_visit": "A Studio Visit",
    "contact.posture_press": "Press or Writing",
    "contact.posture_hint_acquire": "curators · galleries · collectors · writers",
    "contact.posture_hint_exhibition": "galleries · institutions · biennials",
    "contact.posture_hint_visit": "studio visit — India · Singapore · Germany",
    "contact.posture_hint_press": "press · scholarship · catalogue essay",
    "contact.which_studio": "Which studio",
    "contact.send": "Send the letter",
    "contact.sending": "Sending…",
    "contact.success": "The letter has been received. The studio reads slowly.",
    "contact.error_generic": "Something went wrong.",
    "contact.error_missing": "Please complete name, email and message.",
    "contact.error_network": "Network error. Please try again.",

    // ─── 404 / NotFound ────────────────────────────────────────────
    "notfound.section": "404 — Off the map",
    "notfound.title": "This page is not in the archive.",
    "notfound.body": "The link you followed leads nowhere. The studio sometimes moves things between rooms.",
    "notfound.back": "← Back to the front room",

    // ─── Privacy ───────────────────────────────────────────────────
    "privacy.section": "Privacy",
    "privacy.title": "Privacy.",
    "privacy.body": "rushitshah.com does not sell, share or rent personal data. The contact form (Web3Forms) routes your message to the studio inbox; the studio reads slowly and replies when it can. Anonymous, aggregated analytics (PostHog) measure which paintings hold a visitor — they do not identify you. You may write to rs@rushitshah.com to ask what we hold or to have it removed.",

    // ─── Terms ─────────────────────────────────────────────────────
    "terms.section": "Terms & Image Rights",
    "terms.title": "Terms & image rights.",
    "terms.body": "All images on rushitshah.com are © Rushit Shah, all rights reserved. Reproduction for editorial, scholarship or press use is permitted with credit (\"© Rushit Shah, courtesy of the artist\"). Reproduction for any commercial, AI-training, derivative or licensing purpose requires written permission. Please write to rs@rushitshah.com before use.",

    // ─── Acquisitions ──────────────────────────────────────────────
    "acquisitions.section": "Acquisitions",
    "acquisitions.title": "Acquisitions.",
    "acquisitions.body": "Available works, commission information, shipping and certification policy. Write to the studio at rs@rushitshah.com for the current acquisition list and viewing arrangements.",

    // ─── Command Palette ───────────────────────────────────────────
    "cmd.placeholder": "Type a section, title, year or series…",
    "cmd.sections": "Sections",
    "cmd.works": "Works",
    "cmd.empty": "Nothing matches that.",
    "cmd.section.home": "Home — Hero",
    "cmd.section.selected_works": "Selected Works",
    "cmd.section.philosophy": "A Note from the Studio",
    "cmd.section.words": "In the Artist's Words",
    "cmd.section.archive": "Archive — All Works",
    "cmd.section.exhibitions": "Exhibitions",
    "cmd.section.about": "About the Artist",
    "cmd.section.contact": "Contact — write to the studio",
  },

  de: {
    "nav.works": "Werke",
    "nav.rooms": "Ausstellungen",
    "nav.about": "Über",
    "nav.letter": "Kontakt",
    "nav.privacy": "Datenschutz",
    "nav.menu_open": "Menü / +",
    "nav.menu_close": "Schließen",
    "nav.index_count": "Verzeichnis — {count} Abschnitte",
    "nav.studio": "Atelier",
    "nav.skip_to_content": "Zum Inhalt springen",
    "nav.language": "Sprache",
    "nav.studio_quote": "Ich bin farbenblind.\nMein ganzes Leben habe ich Farbe gefühlt, statt sie zu benennen.",

    "footer.index": "Verzeichnis",
    "footer.back_rooms": "Studio",
    "footer.elsewhere": "Anderswo",
    "footer.rights": "© {year} Rushit Shah — Alle Marken sein eigen.",

    "loader.archive_no": "R / S — Archiv №01",
    "loader.entrance": "MMXXVI — Eingang",
    "loader.tagline": "Ein Archiv aus Schichten, befragt.",

    "hero.eyebrow_manifesto": "A · Manifest",
    "hero.manifesto": "Ich male in Schichten und {argue}. Farbe wird gefühlt, nicht benannt — ein Archiv zwischen Indien, Singapur und Deutschland.",
    "hero.manifesto_emph": "befrage sie",
    "hero.cta": "Ins Archiv",
    "hero.look_closer": "Genauer hinsehen ↗",
    "hero.ticker_1": "Ausgewählte Werke",
    "hero.ticker_2": "Zwischen drei Ateliers, drei Ländern gemacht",
    "hero.ticker_3": "Öl · Acryl · Lack · Tropf · gekratztes Pigment",
    "hero.ticker_4": "Langsam gemalt · langsam betrachtet",

    "ribbon.eyebrow": "Abschnitt 02 — Ribbon-Serie",
    "ribbon.hint": "Scrollen → zum Fortschreiten",
    "ribbon.count": "{n} Werke",
    "ribbon.headline_pre": "Ein Faden, der",
    "ribbon.headline_emph": "wiederkehrt",
    "showcase.eyebrow": "Abschnitt 03 — Ausgewählte Werke",
    "showcase.scroll": "Scrollen ↓",
    "showcase.headline_pre": "Schichten, befragt —",
    "showcase.headline_emph": "über die Leinwand",
    "showcase.more_archive": "+ weitere Werke im Archiv",
    "showcase.open_archive": "Vollständiges Archiv ↗",

    "philosophy.eyebrow": "04 — Eine Notiz aus dem Atelier",
    "philosophy.phrase": "Ich male in Schichten und befrage — was die Farbe tut, nicht ihren Namen.",

    "words.eyebrow": "Abschnitt 05 — In den Worten des Künstlers",
    "words.headline_pre": "In den",
    "words.headline_emph": "eigenen",
    "words.headline_post": "Worten des Künstlers.",
    "words.fragment_label": "Fragment",
    "words.I": "Mich interessieren die Orte, an denen organische Systeme würdevoll scheitern — neuronale Karten, rissige Haut, Sternbilder, Verfall. Meine Bilder sind Versuche, dieses Scheitern lange genug ruhig zu halten, um es anzusehen. Ich bin farbenblind. Mein ganzes Leben habe ich Farbe gefühlt, statt sie zu benennen. Vielleicht traue ich ihr deshalb.",
    "words.II": "Jahrelang war ich unterwegs in dem Glauben, was ich suchte, läge anderswo — in einem anderen Land, einer anderen Landschaft, einem anderen Himmel. Die Bilder kamen aus dieser Erschöpfung. Nicht als Flucht, sondern als Ankunft. Zum ersten Mal war ich irgendwo.",
    "words.III": "Jung lehrte mich, dass wir das, was sich nicht direkt sagen lässt, im Bild sagen. Krishnamurti lehrte mich, dass klares Sehen die Zerstörung dessen verlangt, was ich schon zu wissen glaube. Ich bin mit östlichen Miniaturen und Ornamenten aufgewachsen. Ich male in einer internationalen abstrakten Grammatik. Die Reibung zwischen beiden ist, vorerst, das, was ich am dringendsten zu sagen habe.",

    "about.section": "Über · Biografie",
    "about.eyebrow": "geb. 1986, Vadodara",
    "about.headline": "Ein Maler, der dem traut, was Farbe tut.",
    "about.caption_portrait": "Paris · zwischen den Räumen · 2024",
    "about.p1": "Rushit Shah wurde am 8. September 1986 in Vadodara, Gujarat, geboren. Er lebt und arbeitet zwischen Indien, Singapur und Deutschland und teilt das Atelierjahr zwischen den drei Orten auf. Er ist farbenblind und behandelt das nicht als Einschränkung, sondern als Ursprung seiner Methode: Da er sich nicht auf die Namen der Farben verlassen kann, hat er gelernt zu lesen, was eine Farbe neben einer anderen wiegt. Die Bilder sind Aufzeichnungen dieser kleinen, fast physischen Entscheidungen — gebaut aus aufeinanderfolgenden Schichten aus Öl, Lack, Tropf, gekratztem Pigment und gestreutem Mineral auf Leinen und Leinwand, dann so lange befragt, bis etwas nicht mehr nachgibt.",
    "about.p2": "Zwei Erbschaften treffen im Werk aufeinander und lösen sich nicht auf. Die erste ist die Bildwelt, in der er aufgewachsen ist — das Ornament, die Miniatur, die andachtsvolle Fläche, die jeden verfügbaren Zentimeter mit Bild füllt. Die zweite ist das internationale Vokabular der Abstraktion, das er erst als Erwachsener betrat. Er will nicht wählen. Die Reibung zwischen diesen beiden Sprachen ist, vorerst, das, was er am dringendsten zu sagen hat.",
    "about.p3": "Er zitiert Jungs Idee, dass das, was sich nicht direkt sagen lässt, im Bild gesagt wird, und Krishnamurtis Insistieren, dass klares Sehen die Zerstörung dessen verlangt, was man schon zu wissen glaubt — als die beiden Gedanken, die seine Atelier-Tage ordnen. Er arbeitet langsam, in langen, angesammelten Sitzungen, und hält ein Bild erst dann für vollendet, wenn sich die Verhandlung soweit gesetzt hat, dass sie einen Namen verdient.",
    "about.p4": "Der aktuelle Werkkörper wird als ein einziges fortlaufendes Archiv präsentiert — entstanden zwischen drei Ateliers von jemandem, der zu viel gereist ist, um sich auf einen Ort festzulegen, und der malt, um im Gespräch zu bleiben.",
    "about.statement_label": "Künstler-Statement",
    "about.statement": "Ich male in Schichten und befrage. Mich interessieren die Orte, an denen organische Systeme würdevoll scheitern — neuronale Karten, rissige Haut, Sternbilder, Verfall. Meine Bilder sind Versuche, dieses Scheitern lange genug ruhig zu halten, um es anzusehen.",
    "about.statement_2": "Ich traue der Farbe, wie ein Fremder dem Wetter traut: Ich fühle sie, ich benenne sie nicht. Zwei Traditionen sitzen im Werk — die ornamentale Dichte der östlichen Miniaturmalerei, in der ich aufgewachsen bin, und die lockerere Grammatik internationaler Abstraktion, in die ich später kam — und das meiste, was ich zu sagen habe, liegt in der Reibung des Sich-nicht-entscheiden-Wollens.",
    "about.row_born": "Geboren",
    "about.row_born_value": "8. Sept. 1986 · Vadodara",
    "about.row_studio": "Atelier",
    "about.row_studio_value": "IN · SG · DE",
    "about.row_influence": "Einflüsse",
    "about.row_influence_value": "Jung · Krishnamurti · die Straße",
    "about.row_condition": "Bedingung",
    "about.row_condition_value": "Farbenblind. Dort begann die Arbeit.",

    "exhib.section": "Abschnitt — Ausstellungen · Wo das Werk gelebt hat",
    "exhib.eyebrow": "Eine stille Aufzeichnung",
    "exhib.headline_pre": "Ausstellungen, in denen das Werk",
    "exhib.headline_emph": "gelebt",
    "exhib.headline_post": "hat.",
    "exhib.intro": "Eine Aufzeichnung der Ausstellungen — Galerien, Gruppenausstellungen, Projekte —, durch die die Bilder gereist sind. In der Reihenfolge ihres Stattfindens.",
    "exhib.solo": "Einzel",
    "exhib.group": "Gruppe",
    "exhib.forthcoming": "Bevorstehend",
    "exhib.empty_solo": "Die erste Einzelausstellung wird vorbereitet.",
    "exhib.empty_group": "Noch keine Gruppenausstellungen verzeichnet.",
    "exhib.empty_forthcoming": "Die nächste Ausstellung trägt noch keinen Namen.",
    "exhib.footer_line": "— Die Ausstellungen, in Reihenfolge. Aktualisiert, sobald sie stattfinden.",

    "works.section": "Verzeichnis — Werke",
    "works.count": "{filtered} von {total}",
    "works.title": "Ausgewählte Werke.",
    "works.filter_year": "Nach Jahr filtern",
    "works.filter_all": "Alle",
    "works.view_grid": "Raster",
    "works.view_stack": "Stapel",
    "works.no_results": "Keine Werke in diesem Jahr.",

    "work.position": "Nr. {n} / {total}",
    "work.back": "← Zurück zum Archiv",
    "work.prev": "← Vorheriges",
    "work.next": "Nächstes →",
    "work.adj_aria": "Benachbarte Werke",
    "work.keys_hint": "← / → Tasten blättern zwischen den Werken",
    "work.detail_alt": "{title} — Detail {n}",
    "work.not_found": "Dieses Werk ist nicht im Archiv.",
    "work.label_medium": "Medium",
    "work.label_dimensions": "Maße",
    "work.label_edition": "Auflage",
    "work.label_signature": "Signatur",
    "work.label_inventory": "Inventar",
    "work.label_series": "Serie",

    "contact.section": "Abschnitt 07 — Ein Brief, wenn Sie möchten.",
    "contact.headline_pre": "Sprechen",
    "contact.headline_emph": "wir",
    "contact.write_to": "Schreiben an",
    "contact.studio_in_three": "Das Atelier liegt an drei Orten",
    "contact.studio_in_three_value": "Indien · Singapur · Deutschland",
    "contact.the_work": "Die Arbeit",
    "contact.the_work_value": "Öl, Acryl, Lack, Tropf, gekratztes Pigment.\nAuf Leinen und Leinwand, langsam.",
    "contact.elsewhere": "Anderswo",
    "contact.field_name": "Ihr Name",
    "contact.field_email": "E-Mail",
    "contact.field_message": "Nachricht",
    "contact.posture_label": "Die Haltung des Briefes",
    "contact.posture_acquire": "Ein Werk erwerben",
    "contact.posture_exhibition": "Eine Ausstellung planen",
    "contact.posture_visit": "Ein Atelierbesuch",
    "contact.posture_press": "Presse oder Text",
    "contact.posture_hint_acquire": "Kurator:innen · Galerien · Sammler:innen · Autor:innen",
    "contact.posture_hint_exhibition": "Galerien · Institutionen · Biennalen",
    "contact.posture_hint_visit": "Atelierbesuch — Indien · Singapur · Deutschland",
    "contact.posture_hint_press": "Presse · Forschung · Katalogtext",
    "contact.which_studio": "Welches Atelier",
    "contact.send": "Brief senden",
    "contact.sending": "Wird gesendet…",
    "contact.success": "Der Brief ist angekommen. Das Atelier liest langsam.",
    "contact.error_generic": "Da ist etwas schiefgelaufen.",
    "contact.error_missing": "Bitte Name, E-Mail und Nachricht ausfüllen.",
    "contact.error_network": "Netzwerkfehler. Bitte erneut versuchen.",

    "notfound.section": "404 — Außerhalb der Karte",
    "notfound.title": "Diese Seite ist nicht im Archiv.",
    "notfound.body": "Der gefolgte Link führt ins Leere. Das Atelier verschiebt manchmal Dinge zwischen den Räumen.",
    "notfound.back": "← Zurück in den ersten Raum",

    "privacy.section": "Datenschutz",
    "privacy.title": "Datenschutz.",
    "privacy.body": "rushitshah.com verkauft, teilt oder vermietet keine personenbezogenen Daten. Das Kontaktformular (Web3Forms) leitet Ihre Nachricht an das Atelier-Postfach; das Atelier liest langsam und antwortet, sobald es kann. Anonyme, aggregierte Analysedaten (PostHog) messen, welche Bilder eine:n Besucher:in halten — sie identifizieren Sie nicht. Sie können an rs@rushitshah.com schreiben, um zu erfragen, was wir speichern, oder um es löschen zu lassen.",

    "terms.section": "Bedingungen & Bildrechte",
    "terms.title": "Bedingungen & Bildrechte.",
    "terms.body": "Alle Bilder auf rushitshah.com sind © Rushit Shah, alle Rechte vorbehalten. Reproduktion für redaktionelle, wissenschaftliche oder Pressezwecke ist mit Quellenangabe gestattet (\"© Rushit Shah, courtesy of the artist\"). Für jede kommerzielle, KI-Trainings-, abgeleitete oder Lizenzierungsverwendung ist eine schriftliche Genehmigung erforderlich. Bitte schreiben Sie vor Verwendung an rs@rushitshah.com.",

    "acquisitions.section": "Erwerb",
    "acquisitions.title": "Erwerb.",
    "acquisitions.body": "Verfügbare Werke, Informationen zu Auftragsarbeiten, Versand und Zertifikaten. Für die aktuelle Erwerbsliste und Besichtigungsvereinbarungen schreiben Sie an rs@rushitshah.com.",

    "cmd.placeholder": "Abschnitt, Titel, Jahr oder Serie eingeben…",
    "cmd.sections": "Abschnitte",
    "cmd.works": "Werke",
    "cmd.empty": "Nichts passt dazu.",
    "cmd.section.home": "Start — Hero",
    "cmd.section.selected_works": "Ausgewählte Werke",
    "cmd.section.philosophy": "Eine Notiz aus dem Atelier",
    "cmd.section.words": "In den Worten des Künstlers",
    "cmd.section.archive": "Archiv — Alle Werke",
    "cmd.section.exhibitions": "Ausstellungen",
    "cmd.section.about": "Über den Künstler",
    "cmd.section.contact": "Kontakt — ans Atelier schreiben",
  },

  fr: {
    "nav.works": "Œuvres",
    "nav.rooms": "Expositions",
    "nav.about": "À propos",
    "nav.letter": "Contact",
    "nav.privacy": "Confidentialité",
    "nav.menu_open": "Menu / +",
    "nav.menu_close": "Fermer",
    "nav.index_count": "Index — {count} sections",
    "nav.studio": "Atelier",
    "nav.skip_to_content": "Aller au contenu",
    "nav.language": "Langue",
    "nav.studio_quote": "Je suis daltonien.\nJ'ai passé toute ma vie à sentir la couleur plutôt qu'à la nommer.",

    "footer.index": "Index",
    "footer.back_rooms": "Atelier",
    "footer.elsewhere": "Ailleurs",
    "footer.rights": "© {year} Rushit Shah — Toutes marques siennes.",

    "loader.archive_no": "R / S — Archive №01",
    "loader.entrance": "MMXXVI — Entrée",
    "loader.tagline": "Une archive de couches, mises en cause.",

    "hero.eyebrow_manifesto": "A · Manifeste",
    "hero.manifesto": "Je peins en couches, puis {argue}. La couleur se sent, ne se nomme pas — une archive faite entre l'Inde, Singapour et l'Allemagne.",
    "hero.manifesto_emph": "je les conteste",
    "hero.cta": "Entrer dans les archives",
    "hero.look_closer": "Regarder de près ↗",
    "hero.ticker_1": "Œuvres choisies",
    "hero.ticker_2": "Faits entre trois ateliers, trois pays",
    "hero.ticker_3": "Huile · Acrylique · Laque · Coulure · Pigment gratté",
    "hero.ticker_4": "Peintes lentement · vues lentement",

    "ribbon.eyebrow": "Section 02 — Série Ruban",
    "ribbon.hint": "Faites défiler → pour avancer",
    "ribbon.count": "{n} œuvres",
    "ribbon.headline_pre": "Un fil qui",
    "ribbon.headline_emph": "revient",
    "showcase.eyebrow": "Section 03 — Œuvres choisies",
    "showcase.scroll": "Défiler ↓",
    "showcase.headline_pre": "Couches, contestées —",
    "showcase.headline_emph": "à travers la toile",
    "showcase.more_archive": "+ d'œuvres à venir dans les archives",
    "showcase.open_archive": "Ouvrir les archives ↗",

    "philosophy.eyebrow": "04 — Une note depuis l'atelier",
    "philosophy.phrase": "Je peins en couches puis je conteste — ce que la couleur fait, non son nom.",

    "words.eyebrow": "Section 05 — Dans les mots de l'artiste",
    "words.headline_pre": "Dans les",
    "words.headline_emph": "propres",
    "words.headline_post": "mots de l'artiste.",
    "words.fragment_label": "Fragment",
    "words.I": "Je m'intéresse aux endroits où les systèmes organiques échouent avec grâce — cartes neurales, peau fendillée, constellations, décomposition. Mes peintures tentent de tenir cet échec immobile assez longtemps pour le regarder. Je suis daltonien. J'ai passé toute ma vie à sentir la couleur plutôt qu'à la nommer. C'est peut-être pour cela que je lui fais confiance.",
    "words.II": "J'ai passé des années à me déplacer, croyant que ce que je cherchais existait ailleurs — dans un autre pays, un autre paysage, un autre ciel. Les peintures sont sorties de cette fatigue. Non comme fuite, mais comme arrivée. Pour la première fois, j'étais quelque part.",
    "words.III": "Jung m'a appris que ce que nous ne pouvons dire directement, nous le disons en image. Krishnamurti m'a appris que voir clairement exige la destruction de ce que je crois déjà savoir. J'ai grandi en regardant les miniatures et l'ornement orientaux. Je peins dans une grammaire abstraite internationale. La friction entre ces deux choses est, jusqu'ici, ce que j'ai de plus à dire.",

    "about.section": "À propos · Biographie",
    "about.eyebrow": "né en 1986, Vadodara",
    "about.headline": "Un peintre qui fait confiance à ce que fait la couleur.",
    "about.caption_portrait": "Paris · entre deux ateliers · 2024",
    "about.p1": "Rushit Shah est né le 8 septembre 1986 à Vadodara, Gujarat. Il vit et travaille entre l'Inde, Singapour et l'Allemagne, et partage l'année d'atelier entre les trois. Il est daltonien et traite cela non comme une limite mais comme l'origine de sa méthode : incapable de s'appuyer sur le nom des couleurs, il a appris à lire ce qu'une couleur pèse face à sa voisine. Les peintures sont les traces de ces petites décisions presque physiques — construites en couches successives d'huile, de laque, de coulure, de pigment gratté et de minéral dispersé sur lin et toile, puis mises en cause jusqu'à ce que quelque chose refuse de céder.",
    "about.p2": "Deux héritages se rencontrent dans l'œuvre sans se résoudre. Le premier est le monde visuel dans lequel il a grandi — l'ornement, la miniature, la surface dévotionnelle qui peuple chaque centimètre disponible. Le second est le vocabulaire international de l'abstraction qu'il a abordé plus tard, adulte. Il ne veut pas choisir. La friction entre ces deux langues est, jusqu'ici, ce qu'il a de plus à dire.",
    "about.p3": "Il cite l'idée de Jung selon laquelle ce qui ne peut être dit directement est dit en image, et l'insistance de Krishnamurti que voir clairement exige la destruction de ce que l'on croit déjà savoir, comme les deux pensées qui organisent ses journées d'atelier. Il travaille lentement, en longues sessions accumulées, et ne considère une peinture achevée que lorsque la négociation s'est suffisamment posée pour mériter un nom.",
    "about.p4": "Le corpus actuel est présenté comme une seule archive continue — faite entre trois ateliers par quelqu'un qui a trop voyagé pour se fixer en un seul, et qui peint comme une manière de rester dans la conversation.",
    "about.statement_label": "Déclaration d'artiste",
    "about.statement": "Je peins en couches, puis je conteste. Je m'intéresse aux endroits où les systèmes organiques échouent avec grâce — cartes neurales, peau fendillée, constellations, décomposition. Mes peintures tentent de tenir cet échec immobile assez longtemps pour le regarder.",
    "about.statement_2": "Je fais confiance à la couleur comme un étranger fait confiance au temps : je la sens, je ne la nomme pas. Deux traditions habitent l'œuvre — la densité ornementale de la miniature orientale dans laquelle j'ai grandi, et la grammaire plus relâchée de l'abstraction internationale à laquelle je suis venu plus tard — et l'essentiel de ce que j'ai à dire se trouve dans la friction du refus de choisir.",
    "about.row_born": "Né",
    "about.row_born_value": "8 sept. 1986 · Vadodara",
    "about.row_studio": "Atelier",
    "about.row_studio_value": "IN · SG · DE",
    "about.row_influence": "Influence",
    "about.row_influence_value": "Jung · Krishnamurti · la route",
    "about.row_condition": "Condition",
    "about.row_condition_value": "Daltonien. C'est là que le travail a commencé.",

    "exhib.section": "Section — Expositions · Où l'œuvre a vécu",
    "exhib.eyebrow": "Un registre discret",
    "exhib.headline_pre": "Les expositions où l'œuvre",
    "exhib.headline_emph": "a vécu",
    "exhib.headline_post": ".",
    "exhib.intro": "Un registre des expositions — galeries, expositions collectives, projets — par lesquelles les peintures sont passées. Conservées ici dans l'ordre où elles ont eu lieu.",
    "exhib.solo": "Personnelle",
    "exhib.group": "Collective",
    "exhib.forthcoming": "À venir",
    "exhib.empty_solo": "La première exposition personnelle se prépare.",
    "exhib.empty_group": "Aucune exposition collective enregistrée pour l'instant.",
    "exhib.empty_forthcoming": "La prochaine exposition n'a pas encore de nom.",
    "exhib.footer_line": "— Les expositions, dans l'ordre. Mises à jour à mesure qu'elles ont lieu.",

    "works.section": "Index — Œuvres",
    "works.count": "{filtered} sur {total}",
    "works.title": "Œuvres choisies.",
    "works.filter_year": "Filtrer par année",
    "works.filter_all": "Toutes",
    "works.view_grid": "Grille",
    "works.view_stack": "Pile",
    "works.no_results": "Aucune œuvre pour cette année.",

    "work.position": "№ {n} / {total}",
    "work.back": "← Retour aux archives",
    "work.prev": "← Précédente",
    "work.next": "Suivante →",
    "work.adj_aria": "Œuvres adjacentes",
    "work.keys_hint": "Touches ← / → pour passer d'une œuvre à l'autre",
    "work.detail_alt": "{title} — détail {n}",
    "work.not_found": "Cette œuvre n'est pas dans les archives.",
    "work.label_medium": "Médium",
    "work.label_dimensions": "Dimensions",
    "work.label_edition": "Édition",
    "work.label_signature": "Signature",
    "work.label_inventory": "Inventaire",
    "work.label_series": "Série",

    "contact.section": "Section 07 — Une lettre, si vous le souhaitez.",
    "contact.headline_pre": "Parlons-",
    "contact.headline_emph": "en",
    "contact.write_to": "Écrire à",
    "contact.studio_in_three": "L'atelier est en trois lieux",
    "contact.studio_in_three_value": "Inde · Singapour · Allemagne",
    "contact.the_work": "Le travail",
    "contact.the_work_value": "Huile, acrylique, laque, coulure, pigment gratté.\nSur lin et toile, lentement.",
    "contact.elsewhere": "Ailleurs",
    "contact.field_name": "Votre nom",
    "contact.field_email": "E-mail",
    "contact.field_message": "Message",
    "contact.posture_label": "La posture de la lettre",
    "contact.posture_acquire": "Acquérir une œuvre",
    "contact.posture_exhibition": "Planifier une exposition",
    "contact.posture_visit": "Une visite d'atelier",
    "contact.posture_press": "Presse ou texte",
    "contact.posture_hint_acquire": "commissaires · galeries · collectionneurs · auteurs",
    "contact.posture_hint_exhibition": "galeries · institutions · biennales",
    "contact.posture_hint_visit": "visite d'atelier — Inde · Singapour · Allemagne",
    "contact.posture_hint_press": "presse · recherche · texte de catalogue",
    "contact.which_studio": "Quel atelier",
    "contact.send": "Envoyer la lettre",
    "contact.sending": "Envoi…",
    "contact.success": "La lettre est bien arrivée. L'atelier lit lentement.",
    "contact.error_generic": "Quelque chose a mal tourné.",
    "contact.error_missing": "Veuillez remplir nom, e-mail et message.",
    "contact.error_network": "Erreur réseau. Veuillez réessayer.",

    "notfound.section": "404 — Hors carte",
    "notfound.title": "Cette page n'est pas dans les archives.",
    "notfound.body": "Le lien suivi ne mène nulle part. L'atelier déplace parfois les choses d'une salle à l'autre.",
    "notfound.back": "← Retour à la salle d'entrée",

    "privacy.section": "Confidentialité",
    "privacy.title": "Confidentialité.",
    "privacy.body": "rushitshah.com ne vend, ne partage ni ne loue de données personnelles. Le formulaire de contact (Web3Forms) achemine votre message vers la boîte de l'atelier ; l'atelier lit lentement et répond quand il le peut. Des statistiques anonymes et agrégées (PostHog) mesurent quelles peintures retiennent un visiteur — elles ne vous identifient pas. Vous pouvez écrire à rs@rushitshah.com pour demander ce que nous conservons ou pour faire effacer ces données.",

    "terms.section": "Conditions & Droits d'image",
    "terms.title": "Conditions & droits d'image.",
    "terms.body": "Toutes les images sur rushitshah.com sont © Rushit Shah, tous droits réservés. La reproduction à des fins éditoriales, scientifiques ou de presse est autorisée avec crédit (« © Rushit Shah, courtesy of the artist »). Toute reproduction à des fins commerciales, d'entraînement d'IA, dérivées ou de licence nécessite une autorisation écrite. Merci d'écrire à rs@rushitshah.com avant toute utilisation.",

    "acquisitions.section": "Acquisitions",
    "acquisitions.title": "Acquisitions.",
    "acquisitions.body": "Œuvres disponibles, informations sur les commandes, expédition et certification. Écrivez à l'atelier à rs@rushitshah.com pour la liste d'acquisition actuelle et les rendez-vous de visite.",

    "cmd.placeholder": "Tapez une section, un titre, une année ou une série…",
    "cmd.sections": "Sections",
    "cmd.works": "Œuvres",
    "cmd.empty": "Rien ne correspond.",
    "cmd.section.home": "Accueil — Hero",
    "cmd.section.selected_works": "Œuvres choisies",
    "cmd.section.philosophy": "Une note depuis l'atelier",
    "cmd.section.words": "Dans les mots de l'artiste",
    "cmd.section.archive": "Archive — Toutes les œuvres",
    "cmd.section.exhibitions": "Expositions",
    "cmd.section.about": "À propos de l'artiste",
    "cmd.section.contact": "Contact — écrire à l'atelier",
  },

  it: {
    "nav.works": "Opere",
    "nav.rooms": "Mostre",
    "nav.about": "Note",
    "nav.letter": "Contatti",
    "nav.privacy": "Privacy",
    "nav.menu_open": "Menù / +",
    "nav.menu_close": "Chiudi",
    "nav.index_count": "Indice — {count} sezioni",
    "nav.studio": "Studio",
    "nav.skip_to_content": "Vai al contenuto",
    "nav.language": "Lingua",
    "nav.studio_quote": "Sono daltonico.\nHo passato la vita a sentire il colore invece di nominarlo.",

    "footer.index": "Indice",
    "footer.back_rooms": "Studio",
    "footer.elsewhere": "Altrove",
    "footer.rights": "© {year} Rushit Shah — Tutti i segni suoi.",

    "loader.archive_no": "R / S — Archivio №01",
    "loader.entrance": "MMXXVI — Ingresso",
    "loader.tagline": "Un archivio di strati, messi in discussione.",

    "hero.eyebrow_manifesto": "A · Manifesto",
    "hero.manifesto": "Dipingo a strati, poi {argue}. Il colore si sente, non si nomina — un archivio fra India, Singapore e Germania.",
    "hero.manifesto_emph": "li metto in discussione",
    "hero.cta": "Entra nell'archivio",
    "hero.look_closer": "Guarda da vicino ↗",
    "hero.ticker_1": "Opere scelte",
    "hero.ticker_2": "Fatte fra tre studi, tre paesi",
    "hero.ticker_3": "Olio · Acrilico · Lacca · Sgocciolatura · Pigmento raschiato",
    "hero.ticker_4": "Dipinte lentamente · viste lentamente",

    "ribbon.eyebrow": "Sezione 02 — Serie Ribbon",
    "ribbon.hint": "Scorri → per avanzare",
    "ribbon.count": "{n} opere",
    "ribbon.headline_pre": "Un filo che",
    "ribbon.headline_emph": "ritorna",
    "showcase.eyebrow": "Sezione 03 — Opere scelte",
    "showcase.scroll": "Scorri ↓",
    "showcase.headline_pre": "Strati, contestati —",
    "showcase.headline_emph": "attraverso la tela",
    "showcase.more_archive": "+ altre opere in arrivo nell'archivio",
    "showcase.open_archive": "Apri l'archivio ↗",

    "philosophy.eyebrow": "04 — Una nota dallo studio",
    "philosophy.phrase": "Dipingo a strati, poi metto in discussione — quello che il colore fa, non il suo nome.",

    "words.eyebrow": "Sezione 05 — Nelle parole dell'artista",
    "words.headline_pre": "Nelle",
    "words.headline_emph": "proprie",
    "words.headline_post": "parole dell'artista.",
    "words.fragment_label": "Frammento",
    "words.I": "Mi interessano i luoghi in cui i sistemi organici falliscono con grazia — mappe neurali, pelle screpolata, costellazioni, decomposizione. I miei dipinti sono tentativi di tenere quel fallimento fermo abbastanza a lungo da poterlo guardare. Sono daltonico. Ho passato la vita a sentire il colore invece di nominarlo. Forse è per questo che me ne fido.",
    "words.II": "Ho passato anni in movimento, credendo che ciò che cercavo esistesse altrove — in un altro paese, un altro paesaggio, un altro cielo. I dipinti sono nati da quella stanchezza. Non come fuga, ma come arrivo. Per la prima volta, ero in qualche luogo.",
    "words.III": "Jung mi ha insegnato che ciò che non possiamo dire direttamente, lo diciamo per immagini. Krishnamurti mi ha insegnato che vedere chiaramente richiede la distruzione di ciò che già credo di sapere. Sono cresciuto guardando miniature e ornamenti orientali. Dipingo in una grammatica astratta internazionale. L'attrito fra queste due cose è, finora, quello che ho di più da dire.",

    "about.section": "Note · Biografia",
    "about.eyebrow": "n. 1986, Vadodara",
    "about.headline": "Un pittore che si fida di ciò che il colore fa.",
    "about.caption_portrait": "Parigi · fra due studi · 2024",
    "about.p1": "Rushit Shah è nato l'8 settembre 1986 a Vadodara, Gujarat. Vive e lavora fra India, Singapore e Germania, e divide l'anno di studio fra le tre sedi. È daltonico, e tratta questa condizione non come un limite ma come l'origine del suo metodo: non potendo affidarsi ai nomi dei colori, ha imparato a leggere quanto pesa un colore accanto al suo vicino. I dipinti sono il registro di queste decisioni piccole, quasi fisiche — costruiti in strati successivi di olio, lacca, sgocciolatura, pigmento raschiato e minerale disperso su lino e tela, poi messi in discussione finché qualcosa rifiuta di cedere.",
    "about.p2": "Due eredità si incontrano nell'opera senza risolversi. La prima è il mondo visivo in cui è cresciuto — l'ornamento, la miniatura, la superficie devozionale che riempie ogni centimetro disponibile. La seconda è il vocabolario internazionale dell'astrazione, in cui è entrato da adulto. Non vuole scegliere. L'attrito fra queste due lingue è, finora, quello che ha di più da dire.",
    "about.p3": "Cita l'idea di Jung secondo cui ciò che non si può dire direttamente si dice per immagini, e l'insistenza di Krishnamurti che il vedere chiaro richieda la distruzione di ciò che già si crede di sapere, come i due pensieri che organizzano le sue giornate di studio. Lavora lentamente, in lunghe sessioni accumulate, e considera un dipinto finito solo quando la trattativa si è posata abbastanza da meritare un nome.",
    "about.p4": "L'attuale corpo di opere è presentato come un unico archivio in corso — fatto fra tre studi da qualcuno che ha viaggiato troppo per fermarsi in uno solo, e che dipinge per restare nella conversazione.",
    "about.statement_label": "Dichiarazione d'artista",
    "about.statement": "Dipingo a strati, poi metto in discussione. Mi interessano i luoghi in cui i sistemi organici falliscono con grazia — mappe neurali, pelle screpolata, costellazioni, decomposizione. I miei dipinti sono tentativi di tenere quel fallimento fermo abbastanza a lungo da poterlo guardare.",
    "about.statement_2": "Mi fido del colore come uno straniero si fida del tempo: lo sento, non lo nomino. Due tradizioni siedono nell'opera — la densità ornamentale della miniatura orientale in cui sono cresciuto, e la grammatica più libera dell'astrazione internazionale a cui sono arrivato più tardi — e gran parte di ciò che ho da dire si trova nell'attrito del rifiuto di scegliere.",
    "about.row_born": "Nato",
    "about.row_born_value": "8 sett 1986 · Vadodara",
    "about.row_studio": "Studio",
    "about.row_studio_value": "IN · SG · DE",
    "about.row_influence": "Influenze",
    "about.row_influence_value": "Jung · Krishnamurti · la strada",
    "about.row_condition": "Condizione",
    "about.row_condition_value": "Daltonico. Il lavoro è iniziato da lì.",

    "exhib.section": "Sezione — Mostre · Dove l'opera ha vissuto",
    "exhib.eyebrow": "Un registro silenzioso",
    "exhib.headline_pre": "Le mostre in cui l'opera",
    "exhib.headline_emph": "ha vissuto",
    "exhib.headline_post": ".",
    "exhib.intro": "Un registro delle mostre — gallerie, collettive, progetti — attraverso cui le opere sono passate. Conservato qui nell'ordine in cui sono avvenute.",
    "exhib.solo": "Personale",
    "exhib.group": "Collettiva",
    "exhib.forthcoming": "In arrivo",
    "exhib.empty_solo": "La prima personale è in preparazione.",
    "exhib.empty_group": "Nessuna collettiva registrata finora.",
    "exhib.empty_forthcoming": "La prossima mostra non ha ancora un nome.",
    "exhib.footer_line": "— Le mostre, in ordine. Aggiornato man mano che accadono.",

    "works.section": "Indice — Opere",
    "works.count": "{filtered} di {total}",
    "works.title": "Opere scelte.",
    "works.filter_year": "Filtra per anno",
    "works.filter_all": "Tutte",
    "works.view_grid": "Griglia",
    "works.view_stack": "Pila",
    "works.no_results": "Nessuna opera in quest'anno.",

    "work.position": "№ {n} / {total}",
    "work.back": "← Torna all'archivio",
    "work.prev": "← Precedente",
    "work.next": "Successiva →",
    "work.adj_aria": "Opere adiacenti",
    "work.keys_hint": "Tasti ← / → per passare da un'opera all'altra",
    "work.detail_alt": "{title} — dettaglio {n}",
    "work.not_found": "Quest'opera non è nell'archivio.",
    "work.label_medium": "Tecnica",
    "work.label_dimensions": "Dimensioni",
    "work.label_edition": "Edizione",
    "work.label_signature": "Firma",
    "work.label_inventory": "Inventario",
    "work.label_series": "Serie",

    "contact.section": "Sezione 07 — Una lettera, se lo desideri.",
    "contact.headline_pre": "Parliamo",
    "contact.headline_emph": "ne",
    "contact.write_to": "Scrivere a",
    "contact.studio_in_three": "Lo studio è in tre luoghi",
    "contact.studio_in_three_value": "India · Singapore · Germania",
    "contact.the_work": "Il lavoro",
    "contact.the_work_value": "Olio, acrilico, lacca, sgocciolatura, pigmento raschiato.\nSu lino e tela, lentamente.",
    "contact.elsewhere": "Altrove",
    "contact.field_name": "Il tuo nome",
    "contact.field_email": "E-mail",
    "contact.field_message": "Messaggio",
    "contact.posture_label": "La postura della lettera",
    "contact.posture_acquire": "Acquisire un'opera",
    "contact.posture_exhibition": "Pianificare una mostra",
    "contact.posture_visit": "Una visita allo studio",
    "contact.posture_press": "Stampa o testo",
    "contact.posture_hint_acquire": "curatori · gallerie · collezionisti · autori",
    "contact.posture_hint_exhibition": "gallerie · istituzioni · biennali",
    "contact.posture_hint_visit": "visita allo studio — India · Singapore · Germania",
    "contact.posture_hint_press": "stampa · ricerca · testo di catalogo",
    "contact.which_studio": "Quale studio",
    "contact.send": "Invia la lettera",
    "contact.sending": "Invio…",
    "contact.success": "La lettera è arrivata. Lo studio legge lentamente.",
    "contact.error_generic": "Qualcosa è andato storto.",
    "contact.error_missing": "Inserisci nome, e-mail e messaggio.",
    "contact.error_network": "Errore di rete. Riprovare.",

    "notfound.section": "404 — Fuori mappa",
    "notfound.title": "Questa pagina non è nell'archivio.",
    "notfound.body": "Il link seguito non porta da nessuna parte. Lo studio a volte sposta le cose fra le stanze.",
    "notfound.back": "← Torna alla prima sala",

    "privacy.section": "Privacy",
    "privacy.title": "Privacy.",
    "privacy.body": "rushitshah.com non vende, condivide né noleggia dati personali. Il modulo di contatto (Web3Forms) inoltra il messaggio alla casella dello studio; lo studio legge lentamente e risponde quando può. Statistiche anonime e aggregate (PostHog) misurano quali dipinti trattengono un visitatore — non identificano nessuno. Per chiedere cosa conserviamo o per la cancellazione, scrivere a rs@rushitshah.com.",

    "terms.section": "Termini & Diritti d'immagine",
    "terms.title": "Termini & diritti d'immagine.",
    "terms.body": "Tutte le immagini su rushitshah.com sono © Rushit Shah, tutti i diritti riservati. La riproduzione per uso editoriale, scientifico o di stampa è consentita con credito (« © Rushit Shah, courtesy of the artist »). Qualsiasi riproduzione a scopo commerciale, di addestramento di IA, derivata o di licenza richiede autorizzazione scritta. Scrivere a rs@rushitshah.com prima dell'uso.",

    "acquisitions.section": "Acquisizioni",
    "acquisitions.title": "Acquisizioni.",
    "acquisitions.body": "Opere disponibili, informazioni su commissioni, spedizione e certificazione. Per la lista attuale e gli appuntamenti di visione scrivere allo studio a rs@rushitshah.com.",

    "cmd.placeholder": "Digita una sezione, un titolo, un anno o una serie…",
    "cmd.sections": "Sezioni",
    "cmd.works": "Opere",
    "cmd.empty": "Nessun risultato.",
    "cmd.section.home": "Home — Hero",
    "cmd.section.selected_works": "Opere scelte",
    "cmd.section.philosophy": "Una nota dallo studio",
    "cmd.section.words": "Nelle parole dell'artista",
    "cmd.section.archive": "Archivio — Tutte le opere",
    "cmd.section.exhibitions": "Mostre",
    "cmd.section.about": "Sull'artista",
    "cmd.section.contact": "Contatti — scrivere allo studio",
  },

  zh: {
    "nav.works": "作品",
    "nav.rooms": "展览",
    "nav.about": "关于",
    "nav.letter": "联系",
    "nav.privacy": "隐私",
    "nav.menu_open": "菜单 / +",
    "nav.menu_close": "关闭",
    "nav.index_count": "目录 — 共 {count} 节",
    "nav.studio": "工作室",
    "nav.skip_to_content": "跳至正文",
    "nav.language": "语言",
    "nav.studio_quote": "我是色盲。\n一生都在感受颜色,而非命名它。",

    "footer.index": "目录",
    "footer.back_rooms": "工作室",
    "footer.elsewhere": "别处",
    "footer.rights": "© {year} Rushit Shah — 版权归艺术家所有。",

    "loader.archive_no": "R / S — 档案 №01",
    "loader.entrance": "MMXXVI — 入口",
    "loader.tagline": "一份关于层次与争辩的档案。",

    "hero.eyebrow_manifesto": "A · 宣言",
    "hero.manifesto": "我层层作画,然后{argue}。颜色被感受,而非被命名 — 这是一份在印度、新加坡与德国之间生成的档案。",
    "hero.manifesto_emph": "与其争辩",
    "hero.cta": "进入档案",
    "hero.look_closer": "细看 ↗",
    "hero.ticker_1": "精选作品",
    "hero.ticker_2": "在三间工作室、三个国家之间完成",
    "hero.ticker_3": "油彩 · 丙烯 · 漆 · 滴流 · 刮擦颜料",
    "hero.ticker_4": "缓慢绘制 · 缓慢观看",

    "ribbon.eyebrow": "第 02 节 — 缎带系列",
    "ribbon.hint": "滚动 → 推进画卷",
    "ribbon.count": "{n} 件作品",
    "ribbon.headline_pre": "一条线索",
    "ribbon.headline_emph": "反复回来",
    "showcase.eyebrow": "第 03 节 — 精选作品",
    "showcase.scroll": "向下滚动 ↓",
    "showcase.headline_pre": "层次,争辩 —",
    "showcase.headline_emph": "跨越画布",
    "showcase.more_archive": "+ 档案中将陆续更新",
    "showcase.open_archive": "打开完整档案 ↗",

    "philosophy.eyebrow": "04 — 来自工作室的一则笔记",
    "philosophy.phrase": "我层层作画,然后争辩 — 与颜色的作为,而非它的名字。",

    "words.eyebrow": "第 05 节 — 艺术家自述",
    "words.headline_pre": "艺术家的",
    "words.headline_emph": "自己的",
    "words.headline_post": "话语。",
    "words.fragment_label": "片段",
    "words.I": "我对那些有机系统优雅崩塌之处感兴趣 — 神经地图、皲裂的皮肤、星座、衰朽。我的画是要把这种崩塌按住,让它停足够久,以便我们能看见。我色盲。一生都在感受颜色,而非命名它。也许正因此,我才信任它。",
    "words.II": "我曾多年漂泊,以为我所寻找的存在于他处 — 另一个国家、另一片风景、另一种天空。绘画是从这种疲惫中长出来的。不是逃离,而是抵达。第一次,我有了所在。",
    "words.III": "荣格教我:无法直接说的,我们用图像说。克里希那穆提教我:清晰的看见,要先摧毁我以为已知的一切。我从小看东方的细密画与装饰。我以国际抽象的语法作画。这两者之间的摩擦,迄今,是我最想说的事。",

    "about.section": "关于 · 艺术家简历",
    "about.eyebrow": "1986 年生于瓦多达拉",
    "about.headline": "一位信任颜色之所为的画家。",
    "about.caption_portrait": "巴黎 · 两间工作室之间 · 2024",
    "about.p1": "Rushit Shah,1986 年 9 月 8 日生于印度古吉拉特邦瓦多达拉。生活与工作往返于印度、新加坡与德国,一年分作三地。他色盲,并把这看作方法的起点,而非局限:无法依赖颜色的名字,他学会去读一种颜色在邻色旁的重量。绘画便是这些微小、近乎物理之决定的记录 — 由油、漆、滴流、刮擦颜料与分散矿物在亚麻与画布上一层层堆叠而成,再被反复争辩,直到某处不再让步。",
    "about.p2": "作品中两份遗产相遇而不相融。其一是他成长其中的视觉世界 — 装饰、细密画、铺满每寸表面的虔敬之面。其二是他成年后才进入的国际抽象语汇。他不愿选择。这两种语言之间的摩擦,迄今,是他最想说的事。",
    "about.p3": "他常引荣格之言:不能直接说的,用图像说;并引克里希那穆提之语:清晰的看见,要先摧毁自以为已知者。这两念组织着他的画室日子。他作画很慢,在长久积累的会期里完成,只有当一幅画的协商足以承担一个名字时,他才视其为完成。",
    "about.p4": "当前的作品作为一份持续生长的单一档案呈现 — 由一位旅居过多、无法定居一处之人,在三间工作室之间所做,而绘画对他而言,是留在对话里的方式。",
    "about.statement_label": "艺术家自述",
    "about.statement": "我层层作画,然后争辩。我对那些有机系统优雅崩塌之处感兴趣 — 神经地图、皲裂的皮肤、星座、衰朽。我的画是要把这种崩塌按住,让它停足够久,以便我们能看见。",
    "about.statement_2": "我信任颜色,如同陌生人信任天气:我感受它,不命名它。两种传统坐落在作品中 — 我成长其中的东方细密画的装饰密度,与我后来才进入的国际抽象更松散的语法 — 我大半要说的,都在不愿选择的那道摩擦里。",
    "about.row_born": "生",
    "about.row_born_value": "1986 年 9 月 8 日 · 瓦多达拉",
    "about.row_studio": "工作室",
    "about.row_studio_value": "IN · SG · DE",
    "about.row_influence": "影响",
    "about.row_influence_value": "荣格 · 克里希那穆提 · 上路",
    "about.row_condition": "条件",
    "about.row_condition_value": "色盲。作品由此开始。",

    "exhib.section": "节 — 展览 · 作品所栖之地",
    "exhib.eyebrow": "一份安静的记录",
    "exhib.headline_pre": "作品曾",
    "exhib.headline_emph": "栖居",
    "exhib.headline_post": "的展览。",
    "exhib.intro": "一份关于画作所经过的展览 — 画廊、群展、项目 — 的记录。按发生先后保存于此。",
    "exhib.solo": "个展",
    "exhib.group": "群展",
    "exhib.forthcoming": "即将展出",
    "exhib.empty_solo": "首次个展正在筹备。",
    "exhib.empty_group": "尚无群展记录。",
    "exhib.empty_forthcoming": "下一场展览尚未命名。",
    "exhib.footer_line": "— 展览,按序列出。随发生而更新。",

    "works.section": "目录 — 作品",
    "works.count": "共 {total} 件,显示 {filtered}",
    "works.title": "精选作品。",
    "works.filter_year": "按年份筛选",
    "works.filter_all": "全部",
    "works.view_grid": "网格",
    "works.view_stack": "堆叠",
    "works.no_results": "该年份没有作品。",

    "work.position": "№ {n} / {total}",
    "work.back": "← 返回档案",
    "work.prev": "← 上一件",
    "work.next": "下一件 →",
    "work.adj_aria": "相邻作品",
    "work.keys_hint": "← / → 键在作品间切换",
    "work.detail_alt": "{title} — 细节 {n}",
    "work.not_found": "此作不在档案中。",
    "work.label_medium": "媒介",
    "work.label_dimensions": "尺寸",
    "work.label_edition": "版次",
    "work.label_signature": "签名",
    "work.label_inventory": "档号",
    "work.label_series": "系列",

    "contact.section": "第 07 节 — 一封信,如果你愿意。",
    "contact.headline_pre": "我们",
    "contact.headline_emph": "聊聊",
    "contact.write_to": "写信至",
    "contact.studio_in_three": "工作室分布三地",
    "contact.studio_in_three_value": "印度 · 新加坡 · 德国",
    "contact.the_work": "作品",
    "contact.the_work_value": "油彩、丙烯、漆、滴流、刮擦颜料。\n慢慢画在亚麻与画布上。",
    "contact.elsewhere": "别处",
    "contact.field_name": "你的名字",
    "contact.field_email": "邮箱",
    "contact.field_message": "讯息",
    "contact.posture_label": "信的姿态",
    "contact.posture_acquire": "收藏作品",
    "contact.posture_exhibition": "策划展览",
    "contact.posture_visit": "工作室到访",
    "contact.posture_press": "媒体或评述",
    "contact.posture_hint_acquire": "策展人 · 画廊 · 藏家 · 作者",
    "contact.posture_hint_exhibition": "画廊 · 机构 · 双年展",
    "contact.posture_hint_visit": "工作室到访 — 印度 · 新加坡 · 德国",
    "contact.posture_hint_press": "媒体 · 研究 · 画册评述",
    "contact.which_studio": "哪间工作室",
    "contact.send": "寄出此信",
    "contact.sending": "发送中…",
    "contact.success": "来信已收到。工作室读得很慢。",
    "contact.error_generic": "出错了。",
    "contact.error_missing": "请填写姓名、邮箱与讯息。",
    "contact.error_network": "网络错误,请重试。",

    "notfound.section": "404 — 走出了地图",
    "notfound.title": "此页不在档案中。",
    "notfound.body": "你跟随的链接通向无处。工作室有时会在房间之间移动物事。",
    "notfound.back": "← 回到入口之室",

    "privacy.section": "隐私",
    "privacy.title": "隐私。",
    "privacy.body": "rushitshah.com 不出售、共享或出租个人数据。联系表单(Web3Forms)将你的讯息转送至工作室邮箱;工作室读得很慢,但会在能回的时候回复。匿名汇总分析(PostHog)用来衡量哪些画留住了观众 — 不识别个人身份。若想了解我们保存的内容或要求删除,请写信至 rs@rushitshah.com。",

    "terms.section": "条款与图像权利",
    "terms.title": "条款与图像权利。",
    "terms.body": "rushitshah.com 上所有图像 © Rushit Shah,版权所有。出于编辑、研究或媒体目的之复制,在署名「© Rushit Shah, courtesy of the artist」前提下许可。任何商业、AI 训练、衍生或授权用途,须先获得书面许可。使用前请来信 rs@rushitshah.com。",

    "acquisitions.section": "购藏",
    "acquisitions.title": "购藏。",
    "acquisitions.body": "可供作品、委托资讯、运输与证书政策。如需当前购藏清单与预约观看,请写信至 rs@rushitshah.com。",

    "cmd.placeholder": "输入章节、标题、年份或系列名…",
    "cmd.sections": "章节",
    "cmd.works": "作品",
    "cmd.empty": "无匹配结果。",
    "cmd.section.home": "首页 — Hero",
    "cmd.section.selected_works": "精选作品",
    "cmd.section.philosophy": "来自工作室的一则笔记",
    "cmd.section.words": "艺术家自述",
    "cmd.section.archive": "档案 — 所有作品",
    "cmd.section.exhibitions": "展览",
    "cmd.section.about": "关于艺术家",
    "cmd.section.contact": "联系 — 写信给工作室",
  },

  ja: {
    "nav.works": "作品",
    "nav.rooms": "展覧会",
    "nav.about": "プロフィール",
    "nav.letter": "お問い合わせ",
    "nav.privacy": "プライバシー",
    "nav.menu_open": "メニュー / +",
    "nav.menu_close": "閉じる",
    "nav.index_count": "目次 — {count} セクション",
    "nav.studio": "スタジオ",
    "nav.skip_to_content": "本文へスキップ",
    "nav.language": "言語",
    "nav.studio_quote": "私は色覚異常です。\n生涯、色を名づけるのではなく感じてきました。",

    "footer.index": "目次",
    "footer.back_rooms": "スタジオ",
    "footer.elsewhere": "別の場所で",
    "footer.rights": "© {year} Rushit Shah — すべての標は彼のもの。",

    "loader.archive_no": "R / S — アーカイヴ №01",
    "loader.entrance": "MMXXVI — 入口",
    "loader.tagline": "層と問い直しのアーカイヴ。",

    "hero.eyebrow_manifesto": "A · マニフェスト",
    "hero.manifesto": "私は層で描き、そして{argue}。色は名づけるものではなく感じるもの — インド、シンガポール、ドイツの間で作られたアーカイヴ。",
    "hero.manifesto_emph": "それらと議論する",
    "hero.cta": "アーカイヴへ",
    "hero.look_closer": "より近く ↗",
    "hero.ticker_1": "セレクテッド・ワークス",
    "hero.ticker_2": "三つのアトリエ、三つの国の間で制作",
    "hero.ticker_3": "油彩 · アクリル · 漆 · ドリップ · 削り取り顔料",
    "hero.ticker_4": "ゆっくり描く · ゆっくり見る",

    "ribbon.eyebrow": "セクション 02 — リボン・シリーズ",
    "ribbon.hint": "スクロール → で送る",
    "ribbon.count": "{n} 点",
    "ribbon.headline_pre": "戻ってくる",
    "ribbon.headline_emph": "一本の糸",
    "showcase.eyebrow": "セクション 03 — セレクテッド・ワークス",
    "showcase.scroll": "下へスクロール ↓",
    "showcase.headline_pre": "層、問い直されて —",
    "showcase.headline_emph": "キャンバスを横断する",
    "showcase.more_archive": "+ アーカイヴに作品を順次追加中",
    "showcase.open_archive": "アーカイヴを開く ↗",

    "philosophy.eyebrow": "04 — アトリエからの一筆",
    "philosophy.phrase": "私は層で描き、問い直す — 色のふるまいに、その名前にではなく。",

    "words.eyebrow": "セクション 05 — 作家自身の言葉",
    "words.headline_pre": "作家",
    "words.headline_emph": "自身",
    "words.headline_post": "の言葉で。",
    "words.fragment_label": "断章",
    "words.I": "有機的なシステムが優雅に崩れていく場所に、私は惹かれます — 神経地図、ひび割れた皮膚、星座、朽ち。私の絵は、その崩れをじっと留めて、見つめられるだけの長さを与えようとする試みです。私は色覚異常で、色を名づけるのではなく、生涯感じてきました。だから色を信じられるのかもしれません。",
    "words.II": "長年、求めるものはどこか別の場所にあると信じて動き続けました — 別の国、別の風景、別の空。絵はその疲弊から生まれました。逃避ではなく、到着として。初めて、私はどこかにいたのです。",
    "words.III": "ユングは、直接言えないことは像で語ると教えてくれました。クリシュナムルティは、明晰な眼差しには、自分が既に知っていると思うものの破壊が必要だと教えてくれました。私は東方のミニアチュールと装飾を見ながら育ち、国際的な抽象の文法で描きます。その二者の摩擦こそが、今のところ、私が最も語りたいものです。",

    "about.section": "プロフィール · 略歴",
    "about.eyebrow": "1986 年ヴァドーダラー生まれ",
    "about.headline": "色のふるまいを信じる画家。",
    "about.caption_portrait": "パリ · アトリエの合間に · 2024",
    "about.p1": "Rushit Shah は 1986 年 9 月 8 日、インド・グジャラート州ヴァドーダラーに生まれた。インド、シンガポール、ドイツを行き来して暮らし、制作の一年を三つの場所に分けている。色覚異常であることを限界ではなく方法の原点として扱う:色の名に頼れない代わりに、隣り合う色との重みを読むことを覚えた。絵はそうした小さな、ほとんど身体的な決断の記録 — 麻布や帆布の上に、油、漆、ドリップ、削り取られた顔料、分散させた鉱物を重ね、何かが譲らなくなるまで問い続ける。",
    "about.p2": "二つの継承が作品の中で出会い、解けないままある。一つは彼が育った視覚世界 — 装飾、ミニアチュール、すべての隙間を像で埋めようとする敬虔な表面。もう一つは大人になってから入っていった国際的な抽象の語彙。彼は選びたくない。この二つの言語の間の摩擦こそ、今のところ彼が最も語りたいものだ。",
    "about.p3": "彼は、直接言えないものは像によって語られるというユングの考えと、明晰に見るためには既に知っていると思うものを壊さねばならないというクリシュナムルティの主張を、アトリエの一日を組み立てる二つの思想として挙げる。ゆっくり、積み重なった長いセッションで描き、交渉が名前を与えるに値するほど落ち着いたときにのみ、絵が完成したとみなす。",
    "about.p4": "現在の作品群は一つの継続するアーカイヴとして提示される — 三つのアトリエの間で、一つに定住するには旅をしすぎた者によって作られ、絵を描くこと自体が、対話の中に留まるための作法である。",
    "about.statement_label": "アーティスト・ステートメント",
    "about.statement": "私は層で描き、問い直す。有機的なシステムが優雅に崩れていく場所に惹かれる — 神経地図、ひび割れた皮膚、星座、朽ち。私の絵はその崩れをじっと留めて、見つめられるだけの長さを与えようとする試みです。",
    "about.statement_2": "見知らぬ者が天気を信じるように、私は色を信じる:感じはする、名づけない。作品には二つの伝統が同居している — 私が育った東方のミニアチュール絵画の装飾的な密度と、後に入っていった国際的な抽象のより緩い文法 — そして、語るべきことの多くは「選ばないこと」の摩擦の中にある。",
    "about.row_born": "生年",
    "about.row_born_value": "1986 年 9 月 8 日 · ヴァドーダラー",
    "about.row_studio": "スタジオ",
    "about.row_studio_value": "IN · SG · DE",
    "about.row_influence": "影響",
    "about.row_influence_value": "ユング · クリシュナムルティ · 旅路",
    "about.row_condition": "条件",
    "about.row_condition_value": "色覚異常。仕事はそこから始まった。",

    "exhib.section": "セクション — 展覧会 · 作品が暮らした場所",
    "exhib.eyebrow": "静かな記録",
    "exhib.headline_pre": "作品が",
    "exhib.headline_emph": "暮らした",
    "exhib.headline_post": "展覧会。",
    "exhib.intro": "作品が通過した展覧会 — ギャラリー、グループ展、プロジェクト — の記録。起きた順に保管している。",
    "exhib.solo": "個展",
    "exhib.group": "グループ展",
    "exhib.forthcoming": "今後の展示",
    "exhib.empty_solo": "最初の個展を準備中。",
    "exhib.empty_group": "まだグループ展の記録はない。",
    "exhib.empty_forthcoming": "次の展覧会はまだ名づけられていない。",
    "exhib.footer_line": "— 展覧会、順序を保って。発生に応じて更新。",

    "works.section": "目次 — 作品",
    "works.count": "{total} 件中 {filtered}",
    "works.title": "セレクテッド・ワークス。",
    "works.filter_year": "年で絞り込み",
    "works.filter_all": "すべて",
    "works.view_grid": "グリッド",
    "works.view_stack": "スタック",
    "works.no_results": "この年の作品はありません。",

    "work.position": "№ {n} / {total}",
    "work.back": "← アーカイヴへ戻る",
    "work.prev": "← 前の作品",
    "work.next": "次の作品 →",
    "work.adj_aria": "隣接する作品",
    "work.keys_hint": "← / → キーで作品を切り替え",
    "work.detail_alt": "{title} — 細部 {n}",
    "work.not_found": "この作品はアーカイヴにありません。",
    "work.label_medium": "技法",
    "work.label_dimensions": "サイズ",
    "work.label_edition": "エディション",
    "work.label_signature": "署名",
    "work.label_inventory": "インベントリ",
    "work.label_series": "シリーズ",

    "contact.section": "セクション 07 — もしよろしければ、一通の手紙を。",
    "contact.headline_pre": "話",
    "contact.headline_emph": "そう",
    "contact.write_to": "宛先",
    "contact.studio_in_three": "アトリエは三つの場所に",
    "contact.studio_in_three_value": "インド · シンガポール · ドイツ",
    "contact.the_work": "作品",
    "contact.the_work_value": "油彩、アクリル、漆、ドリップ、削り取り顔料。\n麻布とキャンバスに、ゆっくりと。",
    "contact.elsewhere": "別の場所で",
    "contact.field_name": "お名前",
    "contact.field_email": "メール",
    "contact.field_message": "メッセージ",
    "contact.posture_label": "手紙のたたずまい",
    "contact.posture_acquire": "作品を所蔵する",
    "contact.posture_exhibition": "展覧会を企画する",
    "contact.posture_visit": "スタジオ訪問",
    "contact.posture_press": "プレスまたは執筆",
    "contact.posture_hint_acquire": "キュレーター · ギャラリー · コレクター · 執筆者",
    "contact.posture_hint_exhibition": "ギャラリー · 機関 · ビエンナーレ",
    "contact.posture_hint_visit": "スタジオ訪問 — インド · シンガポール · ドイツ",
    "contact.posture_hint_press": "プレス · 研究 · カタログ評",
    "contact.which_studio": "どのスタジオ",
    "contact.send": "手紙を送る",
    "contact.sending": "送信中…",
    "contact.success": "お手紙は届きました。スタジオはゆっくり読みます。",
    "contact.error_generic": "問題が発生しました。",
    "contact.error_missing": "お名前・メール・メッセージをご記入ください。",
    "contact.error_network": "ネットワーク・エラー。もう一度お試しください。",

    "notfound.section": "404 — 地図の外へ",
    "notfound.title": "このページはアーカイヴにありません。",
    "notfound.body": "辿られたリンクは行き先のない場所へ通じています。スタジオは時に部屋から部屋へ物事を移します。",
    "notfound.back": "← 入り口の部屋へ戻る",

    "privacy.section": "プライバシー",
    "privacy.title": "プライバシー。",
    "privacy.body": "rushitshah.com は個人データの販売・共有・貸与を行いません。お問い合わせフォーム(Web3Forms)はメッセージをスタジオの受信箱へ転送します。スタジオはゆっくり読み、可能なときに返信します。匿名・集計の解析(PostHog)は、どの絵が来訪者を留めたかを測るためのものであり、個人を特定するものではありません。保有内容の確認や削除の依頼は rs@rushitshah.com まで。",

    "terms.section": "利用条件 & 画像権利",
    "terms.title": "利用条件 & 画像権利。",
    "terms.body": "rushitshah.com 上のすべての画像は © Rushit Shah、無断複製を禁じます。編集・研究・報道目的の複製はクレジット表記(「© Rushit Shah, courtesy of the artist」)のもと許可されます。商用・AI 学習・派生・ライセンス目的の複製には書面による許可が必要です。使用前に rs@rushitshah.com までご連絡ください。",

    "acquisitions.section": "取得",
    "acquisitions.title": "取得について。",
    "acquisitions.body": "取得可能作品、コミッション、配送と証明の情報。現行の取得リストと閲覧のご相談は rs@rushitshah.com まで。",

    "cmd.placeholder": "セクション・タイトル・年・シリーズを入力…",
    "cmd.sections": "セクション",
    "cmd.works": "作品",
    "cmd.empty": "該当なし。",
    "cmd.section.home": "ホーム — Hero",
    "cmd.section.selected_works": "セレクテッド・ワークス",
    "cmd.section.philosophy": "アトリエからの一筆",
    "cmd.section.words": "作家自身の言葉",
    "cmd.section.archive": "アーカイヴ — 全作品",
    "cmd.section.exhibitions": "展覧会",
    "cmd.section.about": "作家について",
    "cmd.section.contact": "お問い合わせ — スタジオへ手紙",
  },
};
